import React from 'react';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { useIntl } from 'react-intl';
import { reportError } from 'utils/sentry.utils';
import { useMount } from 'ahooks';
import isString from 'lodash/isString';
import { Illustration } from 'components/common/illustration';
import { typeStyle } from 'constants/typography';

interface Props {
  errorExtraInfo?: Record<string, unknown> & { reason?: string };
  errorToReport?: any; // give this prop only if you want to report it to Sentry
  message?: string;
  messageValues?: Record<string, string>;
  icon?: React.ReactElement;
  className?: string;
}

const unexpectedErrorId = 'notifications.error.something.went.wrong';

const ErrorContainer: React.FunctionComponent<Props> = ({
  errorToReport,
  errorExtraInfo,
  message = unexpectedErrorId,
  messageValues,
  icon,
  className
}) => {
  const intl = useIntl();
  const translatedMessage = message && intl.formatMessage({ id: message, defaultMessage: message }, messageValues);

  useMount(() => {
    if (errorToReport) {
      reportError('ErrorContainer', errorToReport, {
        path: window.location.href,
        message: translatedMessage,
        ...errorExtraInfo
      });
    }
  });

  const defaultArt = <Illustration name="something-went-wrong" data-e2e="error-container-illustration" />;

  return (
    <Container data-e2e="error-container" className={className}>
      {icon || defaultArt}
      <EmptyLayoutMessage data-e2e="error-container-actual-msg">{translatedMessage}</EmptyLayoutMessage>
      {isString(errorExtraInfo?.reason) && <Reason>{errorExtraInfo!.reason}</Reason>}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100% - 46px);
  width: 100%;

  ${({ theme }) => css`
    gap: ${theme.spacing.xs};
    color: var(--text-body);
  `}
`;

const EmptyLayoutMessage = styled.h2`
  white-space: pre-line;

  ${typeStyle.headingH4}
  color: var(--text-heading);
  margin: 0;
`;

const Reason = styled.span`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
`;

export default ErrorContainer;
